import { ReactNode } from 'react';
import { useAuthContext } from '@emfood/shared';
import { Navigate } from '@tanstack/react-router';

import { ROLE_RESTRICTIONS } from 'src/api/roles-config';

interface DistributorGuardProps {
  children: ReactNode;
  requiredRoles?: string[];
  orgId: string;
  siteId?: string;
  alwaysAllowRoles?: string[];
}

/**
 * Ensures that the current user has the required permissions and
 * access scopes for a distributor organisation and site
 * @param requiredRoles - Roles required to access. If the user does not have any of these, they will be redirected.
 * @param orgId - Organisation id that organisation-restricted users must have
 * @param siteId - Site id that site-restricted users must have
 * @returns
 */
export default function DistributorGuard({
  children,
  requiredRoles,
  orgId,
  siteId,
  alwaysAllowRoles,
}: DistributorGuardProps) {
  const { user } = useAuthContext();

  // 0. Always allow specified roles
  if (alwaysAllowRoles && user?.roles?.some((role) => alwaysAllowRoles.includes(role.name))) {
    return children;
  }

  // 1. User must have required roles
  if (requiredRoles && !user?.roles?.some((role) => requiredRoles.includes(role.name))) {
    return <Navigate to="/403" replace />;
  }

  // 2. Check if user has at least one unrestricted role
  const hasUnrestrictedRole = user?.roles?.some(
    (role) =>
      !ROLE_RESTRICTIONS[role.name].isOrgRestricted &&
      !ROLE_RESTRICTIONS[role.name].isSiteRestricted
  );

  // If user has an unrestricted role, they bypass all restrictions
  if (hasUnrestrictedRole) {
    return children;
  }

  // 3. Get user restrictions
  const orgRestricted = user?.roles?.some((role) => ROLE_RESTRICTIONS[role.name].isOrgRestricted);
  const siteRestricted = user?.roles?.some((role) => ROLE_RESTRICTIONS[role.name].isSiteRestricted);

  // 4. Org restricted users must have specified org id
  if (orgRestricted && user?.distributorOrg?.id !== orgId) {
    return <Navigate to="/403" replace />;
  }

  // 5. Site restricted users must have specified site id
  if (siteRestricted && user?.distributorSite?.id !== siteId) {
    return <Navigate to="/403" replace />;
  }

  return children;
}
